import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Layout from 'containers/Layout'
import moment from 'moment'
import { get, toUpper } from 'lodash'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import { IoSearch } from 'react-icons/io5'
import PageFooter from 'components/paginationComponent'
import { formatPrice } from 'lib/utils'

const BookingInvoices = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState({})

  useEffect(() => {
    fetchInvoices()
  }, [search, filter])

  const fetchInvoices = async (page = 1) => {
    setLoading(true)
    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    params.query = search || undefined
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }

    try {
      const response = await apiGet('booking-invoices', params)
      setInvoices(page === 1 ? response.data.invoices : [...invoices, ...response.data.invoices])
      setPagination(response?.data?.pagination || {})
    } catch (error) {
      // Handle error silently
    }
    setLoading(false)
  }

  const navigateToInvoiceDetail = invoiceId => {
    history.push(`/booking-invoice/${invoiceId}`)
  }

  const columns = [
    {
      name: 'Car Name',
      key: 'carName',
      sortable: true,
      rendered: item => (
        <span className="text-sm text-gray-900">
          {item.booking?.rentalCar?.car?.make?.make || ''} {item.booking?.rentalCar?.car?.model?.modal || ''} {item.booking?.rentalCar?.modelYear || ''}
        </span>
      ),
    },
    {
      name: 'Booking ID',
      key: 'booking',
      sortable: true,
      rendered: item => <span className="text-sm text-gray-900">{item.booking?._id || ''}</span>,
    },

    {
      name: 'Invoice Status',
      width: '12%',
      key: 'invoiceStatus',
      sortable: true,
      rendered: item => {
        const status = item.invoiceStatus || 'pending'
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          paid: 'bg-green-100 text-green-800',
          overdue: 'bg-red-100 text-red-800',
          cancelled: 'bg-gray-100 text-gray-800',
        }
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[status] || statusColors.pending}`}>
            {toUpper(status.charAt(0)) + status.slice(1)}
          </span>
        )
      },
    },

    {
      name: 'Payment Status',
      width: '12%',
      key: 'invoicePaymentStatus',
      sortable: true,
      rendered: item => {
        const status = item.invoicePaymentStatus || 'pending'
        const statusColors = {
          pending: 'bg-yellow-100 text-yellow-800',
          paid: 'bg-green-100 text-green-800',
          failed: 'bg-red-100 text-red-800',
          cancelled: 'bg-gray-100 text-gray-800',
        }
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[status] || statusColors.pending}`}>
            {toUpper(status.charAt(0)) + status.slice(1)}
          </span>
        )
      },
    },

    {
      name: 'Amount',
      width: '10%',
      key: 'payableAmount',
      sortable: true,
      rendered: item => <span className="text-sm font-medium text-gray-900">OMR{item.payableAmount ? formatPrice(item.payableAmount, true) : '0.000'}</span>,
    },

    {
      name: 'Created',
      width: '10%',
      key: 'createdAt',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        return createdTime ? moment(createdTime).fromNow() : 'N/A'
      },
    },
    {
      name: 'Action',
      width: '15%',
      key: 'action',
      sortable: false,
      rendered: item => (
        <button
          onClick={() => navigateToInvoiceDetail(item._id)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 transition-colors duration-200"
        >
          View Invoice Detail
        </button>
      ),
    },
  ]

  return (
    <Layout
      breadcrumbs={[{ name: 'Booking Invoices', pathname: '/booking-invoices' }]}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className="block w-full pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
                placeholder="Search invoices..."
              />
            </div>
          </div>
        </div>
      }
      title="Booking Invoices"
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {invoices?.length === 0 ? (
                      <EmptyState v2 description="No invoices found" />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchInvoices(pagination?.currentPage + 1)}
                        sortBy={filter.sortBy}
                        sortOrder={filter.sortOrder}
                        handleSort={(sortBy, sortOrder) => {
                          setFilter({ ...filter, sortBy, sortOrder })
                        }}
                        data={invoices}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default BookingInvoices
